import React, { useEffect, useState } from "react";
import { Button, Box, Flex, Card, Heading } from "theme-ui";

import { Decimal, Percent, LiquityStoreState } from "@liquity/lib-base";
import { useLiquitySelector } from "@liquity/lib-react";

import { COIN } from "../../strings";

import { Icon } from "../Icon";
import { LoadingOverlay } from "../LoadingOverlay";
import { EditableRow, StaticRow } from "../Trove/Editor";
import { ActionDescription, Amount } from "../ActionDescription";
import { ErrorDescription } from "../ErrorDescription";
import { useMyTransactionState } from "../Transaction";

import { RedemptionAction } from "./RedemptionAction";
import { InfoIcon } from "../InfoIcon";
import { BlockPolledLiquityStoreState } from "@liquity/lib-ethers";
import BalanceLabel from "../BalanceLabel/BalanceLabel";
import useChain from "../../hooks/useChain";

const select = ({ price, fees, total, usdsBalance }: LiquityStoreState) => ({
  price,
  fees,
  total,
  usdsBalance
});

const selectSystemState = (state: BlockPolledLiquityStoreState) => {
  const { mcrNumber, redemptionFeeFloor } = state;
  return {
    mcrNumber,
    redemptionFeeFloor
  };
};

const transactionId = "redemption";

export const RedemptionManager: React.FC = () => {
  const { price, fees, total, usdsBalance } = useLiquitySelector(select);
  const { mcrNumber, redemptionFeeFloor } = useLiquitySelector(selectSystemState);
  const [usdsAmount, setUSDSAmount] = useState(Decimal.ZERO);
  const [changePending, setChangePending] = useState(false);
  const editingState = useState<string>();

  const MINIMUM_COLLATERAL_RATIO = mcrNumber;

  const mcrPercent = new Percent(MINIMUM_COLLATERAL_RATIO).toString(0);

  const dirty = !usdsAmount.isZero;
  const ethAmount = usdsAmount.div(price);
  const redemptionRate = fees.redemptionRate(redemptionFeeFloor, usdsAmount.div(total.debt));
  const feePct = new Percent(redemptionRate);
  const ethFee = ethAmount.mul(redemptionRate);
  const maxRedemptionRate = redemptionRate.add(0.0035); // 0.1% + 0.25% (max oracle rate).

  const myTransactionState = useMyTransactionState(transactionId);

  const chain = useChain();
  const currencySymbol = chain.nativeCurrency.symbol;

  useEffect(() => {
    if (
      myTransactionState.type === "waitingForApproval" ||
      myTransactionState.type === "waitingForConfirmation"
    ) {
      setChangePending(true);
    } else if (myTransactionState.type === "failed" || myTransactionState.type === "cancelled") {
      setChangePending(false);
    } else if (myTransactionState.type === "confirmed") {
      setUSDSAmount(Decimal.ZERO);
      setChangePending(false);
    }
  }, [myTransactionState.type, setChangePending, setUSDSAmount]);

  const [canRedeem, description] = total.collateralRatioIsBelowMinimum(
    price,
    MINIMUM_COLLATERAL_RATIO
  )
    ? [
        false,
        <ErrorDescription>
          You can't redeem USDS when the total collateral ratio is less than{" "}
          <Amount>{mcrPercent}</Amount>. Please try again later.
        </ErrorDescription>
      ]
    : usdsAmount.gt(usdsBalance)
    ? [
        false,
        <ErrorDescription>
          The amount you're trying to redeem exceeds your balance by{" "}
          <Amount>
            {usdsAmount.sub(usdsBalance).prettify()} {COIN}
          </Amount>
          .
        </ErrorDescription>
      ]
    : [
        true,
        <ActionDescription>
          You will receive{" "}
          <Amount>
            {ethAmount.sub(ethFee).prettify(4)} {currencySymbol}
          </Amount>{" "}
          in exchange for{" "}
          <Amount>
            {usdsAmount.prettify()} {COIN}
          </Amount>
          .
        </ActionDescription>
      ];

  return (
    <Card>
      <Heading>
        Redeem
        {dirty && !changePending && (
          <Button
            variant="titleIcon"
            sx={{ ":enabled:hover": { color: "danger" } }}
            onClick={() => setUSDSAmount(Decimal.ZERO)}
          >
            <Icon name="history" size="lg" />
          </Button>
        )}
      </Heading>

      <Box sx={{ p: [2, 3] }}>
        <EditableRow
          label="Redeem"
          trailingLabel={<BalanceLabel balance={usdsBalance.toString()} tokenSymbol={COIN} />}
          inputId="redeem-usds"
          amount={usdsAmount.prettify()}
          maxAmount={usdsBalance.toString()}
          maxedOut={usdsAmount.eq(usdsBalance)}
          unit={COIN}
          {...{ editingState }}
          editedAmount={usdsAmount.toString(2)}
          setEditedAmount={amount => setUSDSAmount(Decimal.from(amount))}
        />

        <StaticRow
          label="Redemption Fee"
          inputId="redeem-fee"
          amount={ethFee.toString(4)}
          pendingAmount={feePct.toString(2)}
          unit={currencySymbol}
          infoIcon={
            <InfoIcon
              tooltip={
                <Card variant="tooltip" sx={{ minWidth: "240px" }}>
                  The Redemption Fee is charged as a percentage of the redeemed {currencySymbol}. The
                  Redemption Fee depends on USDS redemption volumes and is 0.05% at minimum.
                </Card>
              }
            />
          }
        />

        {((dirty || !canRedeem) && description) || (
          <ActionDescription>Enter the amount of {COIN} you'd like to redeem.</ActionDescription>
        )}

        <Flex variant="layout.actions">
          <RedemptionAction
            transactionId={transactionId}
            disabled={!dirty || !canRedeem}
            usdsAmount={usdsAmount}
            maxRedemptionRate={maxRedemptionRate}
          />
        </Flex>
      </Box>

      {changePending && <LoadingOverlay />}
    </Card>
  );
};
